import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, unref as _unref, withModifiers as _withModifiers, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, normalizeClass as _normalizeClass, vModelText as _vModelText, withDirectives as _withDirectives, createBlock as _createBlock } from "vue"

const _hoisted_1 = { class: "serp__filter-filter--container q-py-1 q-shadow-sm q-my-2 q-mb-2 q-rounded-3 q-bg-white" }
const _hoisted_2 = { class: "q-accordion-item" }
const _hoisted_3 = ["aria-expanded", "aria-controls"]
const _hoisted_4 = { class: "serp__filter-item--title" }
const _hoisted_5 = ["id", "aria-labelledby"]
const _hoisted_6 = { class: "q-accordion-body q-p-1" }
const _hoisted_7 = {
  key: 0,
  class: "q-input-group q-input-group-sm q-my-2 filter-search__container"
}
const _hoisted_8 = ["placeholder"]

import { computed, ref, inject, watch, unref } from 'vue'
import { CustomSmartSearchLabels, SmartSearchTranslationsInterface } from '@/core/types'

// Podrzędne komponenty
import FilterCheckboxRecursive from './FilterItem/FilterCheckboxRecursive.vue'
import BoxItem from './FilterItem/BoxItem.vue'
import InputRangeItem from './FilterItem/InputRangeItem.vue'
import MultiRangeSlider from './FilterItem/MultiRangeSlider.vue'
import {useFiltersStore} from "@/store/SerpPage/FiltersStore"
import {config} from "@/core/config"
import { resolveCustomSmartSearchConfig } from '@/core/customSmartSearchConfig'

// Props

export default /*@__PURE__*/_defineComponent({
  __name: 'FilterItemContainer',
  props: ['filter', 'filterCollapsed'],
  setup(__props) {

const props = __props

const sessionStorageKey = `${config.searchId}_filtersAccordionState`

const customSmartSearchConfig = resolveCustomSmartSearchConfig()
const isAccordionModeEnabled = customSmartSearchConfig.accordionMode
const accordionSearchEnabled = customSmartSearchConfig.accordionSearchEnabled
const accordionClearFiltersButtonEnabled = customSmartSearchConfig.accordionClearFiltersButtonEnabled

function readAccordionState(): Record<string, boolean> {
  if (typeof window === 'undefined' || !window.sessionStorage) {
    return {}
  }

  try {
    const storedValue = window.sessionStorage.getItem(sessionStorageKey)

    if (!storedValue) {
      return {}
    }

    const parsedValue = JSON.parse(storedValue)

    if (typeof parsedValue !== 'object' || parsedValue === null) {
      return {}
    }

    return parsedValue
  } catch (error) {
    console.warn('Unable to read accordion state from sessionStorage', error)
    return {}
  }
}

function saveAccordionState(field: string, isCollapsed: boolean) {
  if (typeof window === 'undefined' || !window.sessionStorage) {
    return
  }

  try {
    const currentState = readAccordionState()
    currentState[field] = isCollapsed
    window.sessionStorage.setItem(sessionStorageKey, JSON.stringify(currentState))
  } catch (error) {
    console.warn('Unable to save accordion state to sessionStorage', error)
  }
}

function getInitialCollapsedState(field: string, defaultValue: boolean): boolean {
  const storedState = readAccordionState()

  if (Object.prototype.hasOwnProperty.call(storedState, field)) {
    return storedState[field]
  }

  return defaultValue
}

const isCollapsed = ref(isAccordionModeEnabled ? getInitialCollapsedState(props.filter.field, props.filterCollapsed || false) : false)

const filtersStore = useFiltersStore()

function toggleAccordion() {
  if (!isAccordionModeEnabled) {
    return
  }

  isCollapsed.value = !isCollapsed.value
}

watch(isCollapsed, (newValue) => {
  if (!isAccordionModeEnabled) {
    return
  }

  saveAccordionState(props.filter.field, newValue)
})

watch(() => props.filter.field, (newField) => {
  if (!isAccordionModeEnabled) {
    return
  }

  isCollapsed.value = getInitialCollapsedState(newField, props.filterCollapsed || false)
})

watch(() => props.filterCollapsed, (newDefault) => {
  if (!isAccordionModeEnabled) {
    isCollapsed.value = false
    return
  }

  const storedState = readAccordionState()

  if (!Object.prototype.hasOwnProperty.call(storedState, props.filter.field)) {
    isCollapsed.value = Boolean(newDefault)
  }
})

const headerButtonClasses = computed(() => ({
  'q-rotatable': isAccordionModeEnabled,
  'q-collapsed': isAccordionModeEnabled && isCollapsed.value,
  'q-rotated': isAccordionModeEnabled && !isCollapsed.value,
  'serp__filter-header--static': !isAccordionModeEnabled
}))

const accordionBodyClasses = computed(() => {
  if (!isAccordionModeEnabled) {
    return 'q-show'
  }

  return {
    'q-collapse': isCollapsed.value,
    'q-show': !isCollapsed.value
  }
})

const accordionAriaExpanded = computed(() => {
  if (!isAccordionModeEnabled) {
    return 'true'
  }

  return (!isCollapsed.value).toString()
})

const lengthOfAggregate = computed(() => props?.filter?.data?.values?.length || 0)


// Tłumaczenia (jak w Twoim kodzie)
const SmartSearchTranslations = inject('SmartSearchTranslations') as any
const customLabels = (typeof window !== 'undefined'
  ? (window as any)?._QONizer?.customSmartSearchConfig?.labels
  : undefined) as CustomSmartSearchLabels | undefined

const normalizeLabel = (label: unknown): string | undefined => {
  if (typeof label !== 'string') {
    return undefined
  }

  const trimmed = label.trim()
  return trimmed || undefined
}

function getFilterSearchHeader(): string {
  const customPrefix = normalizeLabel(customLabels?.filters?.searchPlaceholderPrefix)
  if (customPrefix) {
    return customPrefix
  }

  const prefix = normalizeLabel(unref(SmartSearchTranslations)?.filters?.searchPlaceholderPrefix)

  if (prefix) {
    return prefix
  }

  const base = normalizeLabel(unref(SmartSearchTranslations)?.serp?.filterSearchHeader)

  if (base) {
    return base
  }

  return 'Szukaj w'
}

function getClearLabel(): string {
  const custom = normalizeLabel(customLabels?.filters?.clear)
  if (custom) {
    return custom
  }

  const base = normalizeLabel(unref(SmartSearchTranslations)?.filters?.clear)

  if (base) {
    return base
  }

  return 'Wyczyść filtry'
}

const normalizedFilterLabel = computed(() => {
  const label = props?.filter?.label

  if (typeof label !== 'string') {
    return ''
  }

  try {
    return label.toLocaleLowerCase('pl-PL')
  } catch (error) {
    return label.toLowerCase()
  }
})

const searchPlaceholder = computed(() => {
  const basePlaceholder = getFilterSearchHeader()
  const label = normalizedFilterLabel.value

  if (!label) {
    return basePlaceholder
  }

  const trimmedBase = basePlaceholder?.trim?.() ?? ''

  if (!trimmedBase) {
    return label
  }

  return `${trimmedBase} ${label}`
})

const clearButtonLabel = computed(() => {
  const label = normalizedFilterLabel.value

  if (!label) {
    return getClearLabel()
  }

  const base = getClearLabel()
  const sanitizedBase = typeof base === 'string'
      ? base.replace(/filtr[a-ząćęłńóśźż]*/gi, '').trim()
      : ''

  const prefix = sanitizedBase || (typeof base === 'string' ? base.trim() : '')

  if (!prefix) {
    return label
  }

  return `${prefix} ${label}`
})

const hasActiveValues = computed(() => {
  const activeFilters = filtersStore.getActiveFilters
  const activeValue = activeFilters?.[props.filter.field]

  if (!activeValue) {
    return false
  }

  if (Array.isArray(activeValue)) {
    return activeValue.length > 0
  }

  if (typeof activeValue === 'object') {
    return Object.keys(activeValue).length > 0
  }

  return Boolean(activeValue)
})

function promoteActiveValues(values: any[]): any[] {
  if (!Array.isArray(values) || values.length === 0) {
    return values
  }

  const activeFilters = filtersStore.getActiveFilters
  const activeValues = activeFilters?.[props.filter.field]
  const activeSet = Array.isArray(activeValues) ? new Set(activeValues) : new Set()

  return values
      .map((value) => {
        const clonedValue = {
          ...value,
        }

        if (Array.isArray(value?.aggregations)) {
          clonedValue.aggregations = promoteActiveValues(value.aggregations)
        }

        return clonedValue
      })
      .sort((valueA, valueB) => {
        const valueAIsActive = valueA?.label ? activeSet.has(valueA.label) : false
        const valueBIsActive = valueB?.label ? activeSet.has(valueB.label) : false

        if (valueAIsActive === valueBIsActive) {
          return 0
        }

        return valueAIsActive ? -1 : 1
      })
}

async function clearFilterGroup() {
  await filtersStore.clearFilterByField(props.filter.field)
}

// Klasy CSS do <ul>
const computedClass = computed(() => {
  if (props.filter.type === 'box') {
    return `q-p-0 serp__filter-item--${props.filter.type}`
  }
  return [
    'serp__filter-item--group',
    `serp__filter-item--${props.filter.type}`,
    'q-list-group',
    'q-list-group-flush',
    'q-list-group-collapse',
  ]
})

const computedClassContainer = computed(() => {
  return [`filter__item-container-${props.filter.type}`]
})

// SEARCH
const searchQuery = ref('')

// 1) Definiujemy rekurencyjną funkcję filtrującą
function filterCategoriesRecursive(values: any[], query: string) {
  // jeżeli brak query => nie filtrujemy
  if (!query) {
    return values
  }
  // w przeciwnym wypadku mapujemy i filtrujemy
  return values
      .map(item => {
        const label = typeof item?.label === 'string' ? item.label.toLowerCase() : ''
        const match = label.includes(query)
        const hasAggregations = Array.isArray(item?.aggregations) && item.aggregations.length > 0
        let subItems = []
        if (hasAggregations) {
          subItems = filterCategoriesRecursive(item.aggregations, query)
        }

        // Zostawiamy ten element, jeśli:
        // a) Sam pasuje (match),
        // b) albo jego któryś potomek pasuje (subItems niepuste).
        if (match || subItems.length > 0) {
          // Zwracamy nowy obiekt z *przefiltrowaną* listą subItems
          return {
            ...item,
            aggregations: subItems,
          }
        } else {
          // W przeciwnym wypadku odrzucamy go (undefined)
          return undefined
        }
      })
      // wyrzucamy te, które są undefined
      .filter(Boolean)
}

function filterFlat(values: any[], query: string) {
  if (!query) {
    return values
  }
  return values.filter(item =>
      typeof item?.label === 'string' && item.label.toLowerCase().includes(query)
  )
}

// 2) computed, które zwraca przefiltrowane dane
const filteredValues = computed(() => {
  const allValues = Array.isArray(props.filter?.data?.values) ? props.filter.data.values : []

  if (!Array.isArray(allValues) || allValues.length === 0) {
    return allValues
  }

  const query = (searchQuery.value || '').toLowerCase()
  let processedValues = allValues

  if (props.filter.type === 'category' || props.filter.type === 'checkbox') {
    processedValues = filterCategoriesRecursive(allValues, query)
  } else if (props.filter.type === 'box') {
    processedValues = filterFlat(allValues, query)
  }

  if (props.filter.type === 'checkbox') {
    return promoteActiveValues(processedValues)
  }

  return processedValues
})


return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      _createElementVNode("h6", {
        class: _normalizeClass(["q-accordion-header q-position-relative serp__filter-item--header q-accordion-button q-p-3 q-py-2 q-mt-0 q-fs-6 q-my-0 q-fw-semibold", headerButtonClasses.value]),
        onClick: toggleAccordion,
        "aria-expanded": accordionAriaExpanded.value,
        "aria-controls": __props.filter.field
      }, [
        _createElementVNode("span", _hoisted_4, _toDisplayString(__props.filter.label), 1),
        (_unref(accordionClearFiltersButtonEnabled) && hasActiveValues.value)
          ? (_openBlock(), _createElementBlock("button", {
              key: 0,
              type: "button",
              class: "serp__filter-item--clear-button",
              onClick: _withModifiers(clearFilterGroup, ["stop","prevent"])
            }, _toDisplayString(clearButtonLabel.value), 1))
          : _createCommentVNode("", true)
      ], 10, _hoisted_3),
      _createElementVNode("div", {
        id: __props.filter.field,
        class: _normalizeClass(["q-accordion-collapse", accordionBodyClasses.value]),
        "aria-labelledby": __props.filter.field
      }, [
        _createElementVNode("div", _hoisted_6, [
          _createElementVNode("div", {
            class: _normalizeClass(["serp__filter-item--container q-container-fluid", computedClassContainer.value])
          }, [
            _createElementVNode("ul", {
              class: _normalizeClass([computedClass.value, "q-my-1"])
            }, [
              (_unref(accordionSearchEnabled) && ((__props.filter.type !== 'range' && __props.filter.type !== 'rangeSlider')
                  && (__props.filter.type !== 'category' && lengthOfAggregate.value >= 6)
                  || (__props.filter.type === 'category' && lengthOfAggregate.value >= 2)))
                ? (_openBlock(), _createElementBlock("li", _hoisted_7, [
                    _withDirectives(_createElementVNode("input", {
                      type: "text",
                      class: "q-form-control filter-search__input",
                      placeholder: searchPlaceholder.value,
                      "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((searchQuery).value = $event))
                    }, null, 8, _hoisted_8), [
                      [_vModelText, searchQuery.value]
                    ])
                  ]))
                : _createCommentVNode("", true),
              (__props.filter.type === 'checkbox' || __props.filter.type === 'category')
                ? (_openBlock(), _createBlock(FilterCheckboxRecursive, {
                    key: 1,
                    values: filteredValues.value,
                    field: __props.filter.field
                  }, null, 8, ["values", "field"]))
                : _createCommentVNode("", true),
              (__props.filter.type === 'box')
                ? (_openBlock(), _createBlock(BoxItem, {
                    key: 2,
                    filterDataValues: filteredValues.value,
                    field: __props.filter.field
                  }, null, 8, ["filterDataValues", "field"]))
                : _createCommentVNode("", true),
              (__props.filter.type === 'range')
                ? (_openBlock(), _createBlock(InputRangeItem, {
                    key: 3,
                    filterData: __props.filter.data,
                    field: __props.filter.field
                  }, null, 8, ["filterData", "field"]))
                : _createCommentVNode("", true),
              (__props.filter.type === 'rangeSlider')
                ? (_openBlock(), _createBlock(MultiRangeSlider, {
                    key: 4,
                    filterData: __props.filter.data,
                    field: __props.filter.field
                  }, null, 8, ["filterData", "field"]))
                : _createCommentVNode("", true)
            ], 2)
          ], 2)
        ])
      ], 10, _hoisted_5)
    ])
  ]))
}
}

})